import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useAuthOptional } from '../lib/auth-context';
import { useT } from '../i18n/locale-context';
import { fetchSavedTenderIds, saveTender, unsaveTender } from '../lib/saved-tenders-client';
import { track } from '../lib/funnel';
import { StarIcon } from './icons';
import { cn } from '@/lib/utils';

/** Shared query key: one fetch of the user's saved-tender id set powers every
 *  ☆ on the page (react-query dedupes), so cards need no prop threading. */
export const SAVED_TENDER_IDS_KEY = ['saved-tender-ids'] as const;

/**
 * ☆ save toggle for a tender card. Self-contained: reads the shared saved-id set
 * from the react-query cache and toggles with an optimistic update. Anonymous
 * users are sent to login (saving is free but needs an account). Lives inside a
 * clickable card, so it stops propagation and prevents the card's navigation.
 *
 * Also on the lot page's brand-teal header (2026-10-14): `onBrand` swaps the
 * light-surface colours (ink/bone, and the brand-on-brand «saved» tint that
 * would vanish there) for white-alpha ones, and `withLabel` spells out
 * «В избранное» next to the star — a lone star in a header is easy to miss.
 * Same toggle, same `tender_saved` event, same guest behaviour.
 */
export function SaveStar({
  tenderId,
  className,
  onBrand = false,
  withLabel = false,
}: {
  tenderId: string;
  className?: string;
  onBrand?: boolean;
  withLabel?: boolean;
}) {
  const t = useT();
  const auth = useAuthOptional();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const authed = auth?.status === 'authed';

  const { data: ids } = useQuery({
    queryKey: SAVED_TENDER_IDS_KEY,
    queryFn: fetchSavedTenderIds,
    enabled: authed,
    staleTime: 60_000,
  });
  const saved = !!ids?.includes(tenderId);

  const toggle = useMutation({
    mutationFn: () => (saved ? unsaveTender(tenderId) : saveTender(tenderId)),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: SAVED_TENDER_IDS_KEY });
      const prev = qc.getQueryData<string[]>(SAVED_TENDER_IDS_KEY) ?? [];
      qc.setQueryData<string[]>(
        SAVED_TENDER_IDS_KEY,
        saved ? prev.filter((id) => id !== tenderId) : [tenderId, ...prev],
      );
      if (!saved) track('tender_saved', { id: tenderId });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(SAVED_TENDER_IDS_KEY, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: SAVED_TENDER_IDS_KEY });
      void qc.invalidateQueries({ queryKey: ['saved-tenders'] });
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authed) {
      navigate('/login');
      return;
    }
    toggle.mutate();
  };

  const label = saved ? t('saved.tender.remove') : t('saved.tender.add');
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      aria-pressed={saved}
      title={label}
      className={cn(
        'flex h-8 items-center justify-center rounded-md transition-colors',
        withLabel ? 'gap-1.5 px-2.5 text-[12px] font-semibold' : 'w-8',
        onBrand
          ? 'border border-white/25 text-white/90 hover:bg-white/10 hover:text-white'
          : 'text-ink-muted hover:bg-bone hover:text-brand',
        saved && (onBrand ? 'bg-white/15 text-white' : 'text-brand'),
        className,
      )}
    >
      <StarIcon filled={saved} className="h-[18px] w-[18px]" />
      {withLabel && <span>{label}</span>}
    </button>
  );
}
